import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  Alert,
  Badge,
  Button,
  Card,
  CodeBlock,
  EmptyState,
  Field,
  Input,
  Select,
  Spinner,
  cx,
  useToast,
} from '../../components/ui'
import { ApiError, api } from '../../lib/api'
import { datetime, num } from '../../lib/format'
import type { ApiKey } from '../../lib/types'

const SCOPES = [
  { value: 'qr:generate', label: 'Генерація QR' },
  { value: 'qr:batch', label: 'Пакетна генерація' },
  { value: 'qr:decode', label: 'Розбір QR' },
  { value: 'qr:read', label: 'Читання історії' },
]

export function ApiKeys() {
  const qc = useQueryClient()
  const { push: toast } = useToast()
  const [creating, setCreating] = useState(false)
  const [revealed, setRevealed] = useState<ApiKey | null>(null)

  const { data: keys, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get<ApiKey[]>('/api-keys?includeRevoked=true'),
  })

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['api-keys'] })
    void qc.invalidateQueries({ queryKey: ['overview'] })
  }

  const revoke = useMutation({
    mutationFn: (id: string) => api.del<ApiKey>(`/api-keys/${id}`),
    onSuccess: () => {
      toast('Ключ відкликано', 'success')
      invalidate()
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Помилка', 'danger'),
  })

  const rotate = useMutation({
    mutationFn: (id: string) => api.post<ApiKey>(`/api-keys/${id}/rotate`),
    onSuccess: (key) => {
      setRevealed(key)
      invalidate()
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Помилка', 'danger'),
  })

  const active = keys?.filter((k) => !k.revokedAt) ?? []
  const revoked = keys?.filter((k) => k.revokedAt) ?? []

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">API-ключі</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Ключі <code className="code">qr24_test_…</code> проходять повну валідацію, але не
            тарифікуються.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>Створити ключ</Button>
      </header>

      {revealed?.secret && (
        <Card title="Новий ключ створено" className="ring-2 ring-brand-500">
          <Alert tone="warning" title="Скопіюйте зараз — вдруге ми його не покажемо">
            Секретна частина зберігається лише як HMAC-хеш. Якщо втратите — зробіть ротацію.
          </Alert>
          <div className="mt-4">
            <CodeBlock code={revealed.secret} title={revealed.name} />
          </div>
          <Button variant="secondary" className="mt-4" onClick={() => setRevealed(null)}>
            Я зберіг ключ
          </Button>
        </Card>
      )}

      {creating && (
        <CreateKeyForm
          onClose={() => setCreating(false)}
          onCreated={(key) => {
            setRevealed(key)
            setCreating(false)
            invalidate()
          }}
        />
      )}

      {isLoading ? (
        <div className="grid place-items-center py-20">
          <Spinner className="h-8 w-8 text-brand-600" />
        </div>
      ) : active.length === 0 && revoked.length === 0 ? (
        <Card>
          <EmptyState
            title="Ще немає ключів"
            description="Створіть перший ключ, щоб почати користуватися API."
            action={
              <Button className="mt-2" onClick={() => setCreating(true)}>
                Створити ключ
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {active.map((key) => (
              <KeyRow
                key={key.id}
                apiKey={key}
                onRevoke={() => {
                  if (confirm(`Відкликати ключ «${key.name}»? Дію не можна скасувати.`)) {
                    revoke.mutate(key.id)
                  }
                }}
                onRotate={() => {
                  if (confirm('Створити новий секрет? Старий працюватиме ще 24 години.')) {
                    rotate.mutate(key.id)
                  }
                }}
                busy={revoke.isPending || rotate.isPending}
              />
            ))}
          </div>

          {revoked.length > 0 && (
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium text-slate-500 hover:text-slate-900 dark:hover:text-slate-100">
                Відкликані ключі ({revoked.length})
              </summary>
              <div className="mt-3 space-y-3">
                {revoked.map((key) => (
                  <KeyRow key={key.id} apiKey={key} />
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  )
}

function KeyRow({
  apiKey,
  onRevoke,
  onRotate,
  busy,
}: {
  apiKey: ApiKey
  onRevoke?: () => void
  onRotate?: () => void
  busy?: boolean
}) {
  const isRevoked = Boolean(apiKey.revokedAt)

  return (
    <Card className={cx(isRevoked && 'opacity-60')}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">{apiKey.name}</h3>
            <Badge tone={apiKey.environment === 'live' ? 'success' : 'neutral'}>
              {apiKey.environment}
            </Badge>
            {isRevoked && <Badge tone="danger">відкликано</Badge>}
          </div>
          <p className="mt-1.5 break-all font-mono text-xs text-slate-500 dark:text-slate-400">
            {apiKey.masked}
          </p>

          <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
            <div>
              <dt className="inline">Запитів: </dt>
              <dd className="inline font-medium tabular-nums">{num(apiKey.requestCount)}</dd>
            </div>
            <div>
              <dt className="inline">Останнє використання: </dt>
              <dd className="inline font-medium">{datetime(apiKey.lastUsedAt)}</dd>
            </div>
            <div>
              <dt className="inline">Створено: </dt>
              <dd className="inline font-medium">{datetime(apiKey.createdAt)}</dd>
            </div>
          </dl>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {apiKey.scopes.map((scope) => (
              <Badge key={scope} tone="brand">
                {scope}
              </Badge>
            ))}
            {apiKey.allowedIps.length > 0 && (
              <Badge tone="warning">IP: {apiKey.allowedIps.join(', ')}</Badge>
            )}
          </div>
        </div>

        {!isRevoked && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onRotate} disabled={busy} className="text-xs">
              Ротація
            </Button>
            <Button variant="ghost" onClick={onRevoke} disabled={busy} className="text-xs text-rose-600">
              Відкликати
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}

function CreateKeyForm({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (key: ApiKey) => void
}) {
  const { push: toast } = useToast()
  const [name, setName] = useState('Production')
  const [environment, setEnvironment] = useState<'live' | 'test'>('live')
  const [scopes, setScopes] = useState<string[]>(SCOPES.map((s) => s.value))
  const [allowedIps, setAllowedIps] = useState('')

  const create = useMutation({
    mutationFn: () =>
      api.post<ApiKey>('/api-keys', {
        name,
        environment,
        scopes,
        allowedIps: allowedIps
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      }),
    onSuccess: onCreated,
    onError: (err) =>
      toast(err instanceof ApiError ? err.message : 'Не вдалося створити ключ', 'danger'),
  })

  const toggleScope = (value: string) =>
    setScopes((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value],
    )

  return (
    <Card title="Новий API-ключ">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Назва" hint="Щоб відрізняти ключі в журналі">
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>

        <Field label="Середовище" hint="test не витрачає квоту">
          {(id) => (
            <Select
              id={id}
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as 'live' | 'test')}
            >
              <option value="live">live — бойовий</option>
              <option value="test">test — не тарифікується</option>
            </Select>
          )}
        </Field>

        <div className="sm:col-span-2">
          <p className="label">Дозволи (scopes)</p>
          <div className="flex flex-wrap gap-2">
            {SCOPES.map((scope) => (
              <label
                key={scope.value}
                className={cx(
                  'cursor-pointer rounded-xl border px-3 py-2 text-sm transition',
                  scopes.includes(scope.value)
                    ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                    : 'border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-400',
                )}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={scopes.includes(scope.value)}
                  onChange={() => toggleScope(scope.value)}
                />
                {scope.label}
              </label>
            ))}
          </div>
        </div>

        <div className="sm:col-span-2">
          <Field
            label="Дозволені IP (необовʼязково)"
            hint="Список CIDR через кому, напр. 203.0.113.0/24, 198.51.100.7. Порожньо — будь-який IP."
          >
            {(id) => (
              <Input
                id={id}
                value={allowedIps}
                onChange={(e) => setAllowedIps(e.target.value)}
                placeholder="203.0.113.0/24"
                className="font-mono text-sm"
              />
            )}
          </Field>
        </div>
      </div>

      <div className="mt-5 flex gap-2">
        <Button onClick={() => create.mutate()} loading={create.isPending} disabled={scopes.length === 0}>
          Створити
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Скасувати
        </Button>
      </div>
    </Card>
  )
}
